<script lang="ts">
  import { onMount, tick } from "svelte";
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import CountUpStatItem from "$lib/molecules/CountUpStatItem.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import { storyblokEditable } from "@storyblok/svelte";
  import { pageMainColor } from "$stores";
  import type { StatsListStoryblok } from "$types/components";

  export let blok: StatsListStoryblok;
  export let dividers: "normal" | "subtle" = "normal";

  $: stats = blok.items;
  $: apiResponse = {} as Record<string, number>;

  const isHighlight = blok.type === "inline-highlight";

  async function getStats() {
    try {
      const response = await fetch(
        "https://delivery-counter.platform.flyzipline.com/api/v1/public/stats/",
      );
      const res = await response.json();

      await tick();

      apiResponse = res;

      setTimeout(() => {
        getStats();
      }, 2000);
    } catch (error) {
      // do nothing
    }
  }

  onMount(() => {
    getStats();
  });
</script>

{#if blok.type === "inline" || isHighlight}
  <section
    class="section stats-list"
    class:subtle={dividers === "subtle"}
    class:inline-highlight={isHighlight}
    class:max-w-[1240px]={isHighlight}
    class:pt-[30px]={!isHighlight}
    class:pb-[30px]={!isHighlight}
    class:md:pb-0={!isHighlight}
    class:md:pt-12={!isHighlight}
    class:lg:pt-0={!isHighlight}
    class:py-[52px]={isHighlight}
    class:md:my-[48px]={isHighlight}
    class:lg:my-[56px]={isHighlight}
    class:xl:my-[64px]={isHighlight}
    class:background-warm-grey={isHighlight && $pageMainColor === "light-gray"}
    class:background-gray={isHighlight && $pageMainColor === "gray"}
    class:background-green={isHighlight && $pageMainColor === "green"}
    class:background-yellow={isHighlight && $pageMainColor === "yellow"}
    class:background-blue={isHighlight && $pageMainColor === "blue"}
    use:storyblokEditable={blok}
  >
    <GridContainer>
      <GridItem col={12} colMd={12}>
        <div
          class="grid md:grid-cols-3"
          class:md:gap-8={!isHighlight}
          class:md:gap-16={isHighlight}
        >
          {#if stats}
            {#each stats as stat}
              <div
                class="inline-stat-item"
                class:py-[16px]={!isHighlight}
                class:py-[2rem]={isHighlight}
              >
                <p
                  class="mb-2 md:mb-3"
                  class:md:mb-[24px]={isHighlight}
                  class:type:h2={!isHighlight}
                  class:type:h1={isHighlight}
                  class:text-nowrap={isHighlight}
                  class:text-inherit={isHighlight}
                >
                  {#if stat.value?.includes("api.")}
                    {#if !apiResponse[stat.value.replace("api.", "")]}
                      -
                    {:else}
                      <CountUpStatItem
                        value={apiResponse[stat.value.replace("api.", "")]}
                      />
                    {/if}
                  {:else}
                    {stat.value}
                  {/if}
                </p>
                <p
                  class="label type:h6-medium"
                  class:mb-0={isHighlight}
                  class:text-inherit={isHighlight}
                >
                  {stat.label}
                </p>
                {#if isHighlight}
                  <p class="type:body-medium">{stat.description}</p>
                {/if}
              </div>
            {/each}
          {/if}
        </div>
      </GridItem>
    </GridContainer>
  </section>
{:else if blok.type === "list"}
  <div class="grid gap-4 md:gap-[50px] grid-cols-3 md:grid-cols-1">
    {#if blok.items}
      {#each blok.items as stat}
        <div
          class="pl-2 md:pl-[2.5rem]"
          style="border-left: 1px solid #242021;"
        >
          <GridContainer alignItems="center" noPadding={true}>
            <GridItem col={12} colMd={12} alignSelf="center">
              <p class="type:h3-stats">
                {#if stat.value?.includes("api.") && !apiResponse[stat.value.replace("api.", "")]}
                  -
                {:else}
                  <CountUpStatItem
                    value={apiResponse[stat.value?.replace("api.", "") ?? ""]}
                  />
                {/if}
              </p>
              <p class="type:body-medium-stats mt-2.5 text-dark-grey">
                {stat.label}
              </p>
              {#if stat.description}<p class="description type:body-medium">
                  {stat.description}
                </p>{/if}
            </GridItem>
          </GridContainer>
        </div>
      {/each}
    {/if}
  </div>
{:else}
  <section class="section stats-list" use:storyblokEditable={blok}>
    <GridContainer>
      <GridItem col={12} colMd={9}>
        <div class="header section-header">
          <p class="eyebrow type:section-title">{blok.eyebrow}</p>
          <h2 class="headline type:h2">{@html blok.headline}</h2>
        </div>
      </GridItem>
      <GridItem col={12}>
        <div class="stat-list">
          {#if blok.items}
            {#each blok.items as stat}
              <div class="stat">
                <GridContainer alignItems="end" noPadding={true}>
                  <GridItem col={12} colMd={7} alignSelf="end">
                    <p class="type:h1-large value">{stat.value}</p>
                  </GridItem>
                  <GridItem
                    col={12}
                    offset={0}
                    colMd={4}
                    offsetMd={1}
                    alignSelf="end"
                  >
                    <p class="label type:h6-medium">{stat.label}</p>
                    <p class="description type:body-medium">
                      {stat.description}
                    </p>
                  </GridItem>
                </GridContainer>
              </div>
            {/each}
          {/if}
        </div>
      </GridItem>
    </GridContainer>
  </section>
{/if}

<style lang="scss">
  .inline-highlight {
    @media (min-width: 1240px) {
      border-radius: var(--border-radius-section);
    }
  }
  .inline-stat-item {
    border-top: 1px solid rgba(0, 0, 0, 0.2);
    &:last-of-type {
      border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    }
    .inline-highlight.background-blue &,
    .inline-highlight.background-green & {
      border-color: rgba(255, 255, 255, 0.5);
    }
    .subtle & {
      border-color: rgba(0, 0, 0, 0.2);
    }

    @include breakpoint("small") {
      padding: 0;
      padding-left: clamp(2rem, 2.5vw, 4rem);
      .inline-highlight & {
        padding: 12px 0;
      }
      border: none;
      &:last-of-type {
        border-bottom: none;
      }
      border-left: 1px solid #000;
      &:nth-of-type(3) {
        border-right: 1px solid #000;
        .subtle & {
          border-right: none;
        }
      }
      .inline-highlight &:first-of-type {
        border-left: none;
      }
      .inline-highlight &:last-of-type {
        border-right: none;
      }
      .inline-highlight &:nth-of-type(n + 2) {
        padding-left: 2rem;
        margin-left: -2rem;
      }
    }
  }

  .stat-list {
    width: 100%;
  }

  .value {
    margin-bottom: 8px;

    @include breakpoint("small") {
      margin-bottom: 0;
    }
  }

  .stat {
    border-top: 1px solid rgba(#242021, 0.1);
    padding: fluid(56px, 16px) 0;

    &:last-child {
      padding-bottom: 0;
    }
  }

  .label {
    margin-bottom: em(8px, 16px);
  }

  .description {
    @include breakpoint("small") {
      margin-bottom: fluid(20px, 12px);
    }
  }
</style>
